import { useEffect } from "react";

import { ToggleButton } from "@newsela/angelou";
import { useRouter } from "next/router";
import PropTypes from "prop-types";

import { withTrackEvent, actionNames, objectTypes } from "@newsela/snitch";
import { getIsSavedAssignment } from "@/assignments/utils/assignmentUrlHelpers";
import useStudentPreviewContext from "@/view/utils/useStudentPreviewContext";
import {
  buttonCss,
  containerCss,
} from "./AssignmentStudentPreviewToggle.style";

const propTypes = {
  currentContentLevel: PropTypes.shape({
    header_id: PropTypes.string,
    id: PropTypes.string,
  }),
};

const ToggleButtonWithTrackEvent = withTrackEvent(ToggleButton);

const AssignmentStudentPreviewToggle = ({ currentContentLevel }) => {
  const router = useRouter();
  const { query, pathname } = router;
  const { selectedAssignmentId } = query;
  const isSavedAssignment = getIsSavedAssignment(selectedAssignmentId);

  const { isStudentPreviewOn, setIsStudentPreviewOn } =
    useStudentPreviewContext();

  const eventProperties = {
    actionName: actionNames.interact,
    objectType: objectTypes.assignment,
    streamProperties: {
      content_type: "Article",
      content_ids: [currentContentLevel?.header_id],
      content_id: currentContentLevel?.id,
      description: `Toggle student preview mode ${isStudentPreviewOn ? "off" : "on"}`,
      ...(isSavedAssignment ? { object_id: selectedAssignmentId } : {}),
    },
  };

  const handleToggle = () => {
    setIsStudentPreviewOn(!isStudentPreviewOn);
  };

  useEffect(() => {
    // When previewing a saved assignment, append a studentPreview param to url.
    if (isSavedAssignment && isStudentPreviewOn) {
      const href = {
        pathname,
        query: {
          ...query,
          studentPreview: true,
        },
      };

      router.replace(href, null, { shallow: true });
    } else {
      // Remove the studentPreview param when preview is toggled off.
      const params = query;
      delete params.studentPreview;
      const href = { pathname, query: { ...params } };
      router.replace(href, null, { shallow: true });
    }
  }, [isStudentPreviewOn]);

  return (
    <div css={containerCss}>
      <ToggleButtonWithTrackEvent
        {...eventProperties}
        __cssFor={{
          Button: {
            root: buttonCss,
          },
        }}
        dataProps={{
          "data-qa-selector": isStudentPreviewOn
            ? "toggle-student-preview-mode-off"
            : "toggle-student-preview-mode-on",
        }}
        label="View as student"
        onClick={handleToggle}
        pressed={isStudentPreviewOn}
      />
    </div>
  );
};

AssignmentStudentPreviewToggle.propTypes = propTypes;

export default AssignmentStudentPreviewToggle;
